/** Small dependency-free text helpers shared by API, web and seed. */

/** Lowercase + trim. Full validation happens with zod `z.email()`. */
export function normalizeEmail(email: string | null | undefined): string | null {
  if (!email) return null;
  const v = email.trim().toLowerCase();
  return v.length > 0 ? v : null;
}

const GREEK_TO_LATIN: Record<string, string> = {
  α: 'a',
  β: 'v',
  γ: 'g',
  δ: 'd',
  ε: 'e',
  ζ: 'z',
  η: 'i',
  θ: 'th',
  ι: 'i',
  κ: 'k',
  λ: 'l',
  μ: 'm',
  ν: 'n',
  ξ: 'x',
  ο: 'o',
  π: 'p',
  ρ: 'r',
  σ: 's',
  ς: 's',
  τ: 't',
  υ: 'y',
  φ: 'f',
  χ: 'ch',
  ψ: 'ps',
  ω: 'o',
};

/** Greek-aware transliteration (ELOT-like, simplified) for slugs/keys. */
export function transliterateGreek(input: string): string {
  const base = input
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/ου/g, 'ou')
    .replace(/αυ/g, 'av')
    .replace(/ευ/g, 'ev')
    .replace(/γγ/g, 'ng');
  let out = '';
  for (const ch of base) out += GREEK_TO_LATIN[ch] ?? ch;
  return out;
}

/** URL slug: Greek transliterated, ascii, dash separated, max 48 chars. */
export function slugify(input: string, maxLength = 48): string {
  const s = transliterateGreek(input)
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, maxLength)
    .replace(/-+$/g, '');
  return s.length > 0 ? s : 'business';
}

/** Safe machine key (snake_case) for custom fields / events, e.g. "Αγαπημένος κουρέας" → "agapimenos_koureas". */
export function toMachineKey(input: string, maxLength = 48): string {
  const s = transliterateGreek(input)
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, maxLength)
    .replace(/_+$/g, '');
  const key = s.length > 0 ? s : 'field';
  return /^[0-9]/.test(key) ? `f_${key}` : key;
}

/** Characters that can start a spreadsheet formula (incl. the full-width forms Excel accepts). */
export const CSV_FORMULA_START = /^[=+\-@\t\r＝＋－＠]/;

/**
 * Neutralize CSV formula injection when exporting user-provided values: a value whose first
 * non-space character could start a formula is prefixed with `'`.
 */
export function csvSafe(value: string): string {
  return CSV_FORMULA_START.test(value.trimStart()) ? `'${value}` : value;
}

/**
 * One formula-safe CSV cell. Quotes `;` too: Excel in locales whose list separator is `;` (Greek)
 * would otherwise split `text;=formula` and evaluate the second half.
 */
export function csvCell(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return '';
  const v = csvSafe(String(value));
  return /[",\r\n;]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
}

/** RFC 4180 CSV with a UTF-8 BOM (Excel opens Greek correctly) and CRLF line endings. */
export function toCsv(header: string[], rows: (string | number | null | undefined)[][]): string {
  const lines = [header.map(csvCell).join(','), ...rows.map((r) => r.map(csvCell).join(','))];
  return `\ufeff${lines.join('\r\n')}\r\n`;
}
